import { useCallback, useEffect, useState } from 'react';

export type Route =
  | { name: 'messenger' }
  | { name: 'connect'; adding: boolean; initialAddress?: string }
  | { name: 'admin' }
  | { name: 'download' }
  | { name: 'invite'; token: string | null }
  | { name: 'reset'; token: string | null }
  | { name: 'setup' };

/** Tokens travel in the URL fragment so they never reach server logs; read and keep in memory. */
function fragmentToken(hash: string): string | null {
  const raw = hash.replace(/^#/, '');
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  return params.get('token') ?? (raw.includes('=') ? null : decodeURIComponent(raw));
}

export function parseLocation(location: Pick<Location, 'pathname' | 'hash'>): Route {
  const path = location.pathname.replace(/\/+$/, '') || '/';
  switch (path) {
    case '/invite':
      return { name: 'invite', token: fragmentToken(location.hash) };
    case '/reset-password':
      return { name: 'reset', token: fragmentToken(location.hash) };
    case '/setup':
      return { name: 'setup' };
    case '/download':
      return { name: 'download' };
    case '/admin':
      return { name: 'admin' };
    default:
      return { name: 'messenger' };
  }
}

export function pathOf(route: Route): string {
  switch (route.name) {
    case 'admin':
      return '/admin';
    case 'download':
      return '/download';
    case 'setup':
      return '/setup';
    case 'invite':
      return '/invite';
    case 'reset':
      return '/reset-password';
    default:
      return '/';
  }
}

/**
 * Web: real paths with history navigation (the fragment token is stripped
 * from the address bar after it is read). Desktop: in-memory routes.
 */
export function useRouter(kind: 'web' | 'desktop', initial?: Route) {
  const [route, setRoute] = useState<Route>(() => initial ?? (kind === 'web' && typeof window !== 'undefined' ? parseLocation(window.location) : { name: 'messenger' }));

  useEffect(() => {
    if (kind !== 'web') return undefined;
    const initialRoute = parseLocation(window.location);
    if ((initialRoute.name === 'invite' || initialRoute.name === 'reset') && window.location.hash) {
      // Keep the token out of the visible URL / history once read (it is already in state).
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    }
    const onPop = () => setRoute(parseLocation(window.location));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [kind]);

  const navigate = useCallback(
    (next: Route, options: { replace?: boolean } = {}) => {
      setRoute(next);
      if (kind !== 'web') return;
      const url = pathOf(next) + window.location.search;
      if (url === window.location.pathname + window.location.search) return;
      if (options.replace) window.history.replaceState(null, '', url);
      else window.history.pushState(null, '', url);
    },
    [kind],
  );
  return { route, navigate };
}
